/**
 * useGroupChatHandler - 群聊消息处理 Hook
 * 整合群聊相关的所有消息处理逻辑，从 ChatPage.tsx 中独立出来
 */

import { useCallback, useRef } from 'react';
import {
  Message,
  MessageBlock,
  Choices,
  GroupChatMessage,
  Conversation,
} from '@/types';
import { useGroupChat } from '@/hooks/chat/useGroupChat';
import { useAuthStore, useUIStore } from '@/stores';

interface UseGroupChatHandlerOptions {
  conversationId: string | undefined;
  isGroupChat: boolean;
  conversationsRef: React.MutableRefObject<Conversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  addMessageToConversation: (conversationId: string, message: Message) => void;
  updateConversationLoadingState: (
    conversationId: string,
    isLoading: boolean
  ) => void;
  scrollToBottom: () => void;
}

export function useGroupChatHandler({
  conversationId,
  isGroupChat,
  conversationsRef: _conversationsRef,
  setConversations,
  addMessageToConversation,
  updateConversationLoadingState,
  scrollToBottom,
}: UseGroupChatHandlerOptions) {
  const currentUserInfo = useAuthStore(state => state.userInfo);
  const currentUserIdRef = useRef(currentUserInfo?.id);
  const conversationIdRef = useRef(conversationId);

  // 🔥 使用 ref 存储回调函数，避免依赖变化导致 useCallback 重新创建
  const addMessageToConversationRef = useRef(addMessageToConversation);
  const updateConversationLoadingStateRef = useRef(
    updateConversationLoadingState
  );
  const scrollToBottomRef = useRef(scrollToBottom);
  const setConversationsRef = useRef(setConversations);

  // 更新 refs
  conversationIdRef.current = conversationId;
  currentUserIdRef.current = currentUserInfo?.id;
  addMessageToConversationRef.current = addMessageToConversation;
  updateConversationLoadingStateRef.current = updateConversationLoadingState;
  scrollToBottomRef.current = scrollToBottom;
  setConversationsRef.current = setConversations;

  /**
   * 处理其他用户发送的消息
   */
  const handleGroupChatUserMessage = useCallback(
    (data: GroupChatMessage) => {
      const convId = conversationIdRef.current;
      if (!convId) return;

      console.log('🔔 handleGroupChatUserMessage:', {
        messageId: data.messageId,
        previousMessageId: data.previousMessageId,
        content: data.content?.substring(0, 50),
      });

      // 构建消息 blocks
      const blocks: MessageBlock[] = [];
      if (data.fileIds && data.fileIds.length > 0) {
        data.fileIds.forEach(fileId => {
          blocks.push({
            type: 'file',
            content: String(fileId),
            id: String(fileId),
            fileId: String(fileId),
          });
        });
      }
      if (data.content) {
        blocks.push({ type: 'text', content: data.content });
      }
      if (blocks.length === 0) {
        blocks.push({ type: 'text', content: '' });
      }

      const choice: Choices = { blocks, version: 1 };
      const userMessage: Message = {
        id: String(data.messageId || Date.now()),
        backendId: data.messageId,
        previousMessageId: data.previousMessageId,
        role: 'user',
        choices: [choice],
        timestamp: new Date(),
        isStreaming: false,
      };

      // 添加用户信息
      (userMessage as any).user = data.user;

      addMessageToConversationRef.current(convId, userMessage);
      scrollToBottomRef.current();
    },
    [] // 🔥 空依赖数组，通过 ref 访问最新函数，避免依赖变化导致无限循环
  );

  /**
   * 处理自己发送的消息回显
   */
  const handleGroupChatSelfMessage = useCallback(
    (data: GroupChatMessage) => {
      const convId = conversationIdRef.current;
      if (!convId) return;

      console.log('🔔 handleGroupChatSelfMessage:', {
        messageId: data.messageId,
        previousMessageId: data.previousMessageId,
        content: data.content?.substring(0, 50),
      });

      // 构建消息 blocks
      const blocks: MessageBlock[] = [];
      if (data.fileIds && data.fileIds.length > 0) {
        data.fileIds.forEach(fileId => {
          blocks.push({
            type: 'file',
            content: String(fileId),
            id: String(fileId),
            fileId: String(fileId),
          });
        });
      }
      if (data.content) {
        blocks.push({ type: 'text', content: data.content });
      }
      if (blocks.length === 0) {
        blocks.push({ type: 'text', content: '' });
      }

      const choice: Choices = { blocks, version: 1 };
      const selfMessage: Message = {
        id: String(data.messageId || Date.now()),
        backendId: data.messageId,
        previousMessageId: data.previousMessageId,
        role: 'user',
        choices: [choice],
        timestamp: new Date(),
        isStreaming: false,
      };

      // 添加用户信息
      (selfMessage as any).user = data.user;
      (selfMessage as any).isOwnMessage = true;
      // 🔥 添加 client 信息，用于 @Agent 标签显示
      (selfMessage as any).client = data.client;

      // 设置 loading 状态
      updateConversationLoadingStateRef.current(convId, true);
      addMessageToConversationRef.current(convId, selfMessage);
      scrollToBottomRef.current();
    },
    [] // 🔥 空依赖数组，通过 ref 访问最新函数，避免依赖变化导致无限循环
  );

  /**
   * 处理 AI 消息初始化
   */
  const handleGroupChatMessageInit = useCallback(
    (data: GroupChatMessage) => {
      const convId = conversationIdRef.current;
      if (!convId) return;

      console.log('🔔 handleGroupChatMessageInit:', data);

      const emptyChoice: Choices = { blocks: [], version: 1 };
      const assistantMessage: Message = {
        id: String(data.messageId || Date.now()),
        backendId: data.messageId,
        previousMessageId: data.previousMessageId,
        completionId: data.completionId,
        role: 'assistant',
        choices: [emptyChoice],
        timestamp: new Date(),
        isStreaming: true,
      };

      // 添加客户端信息
      if (data.client) {
        assistantMessage.client = {
          id: String(data.client.id),
          name: data.client.name,
          logo: data.client.logo,
        };
      }

      addMessageToConversationRef.current(convId, assistantMessage);
      scrollToBottomRef.current();
    },
    [] // 🔥 空依赖数组，通过 ref 访问最新函数，避免依赖变化导致无限循环
  );

  /**
   * 处理 AI 文本内容
   */
  const handleGroupChatContent = useCallback(
    (data: GroupChatMessage) => {
      const convId = conversationIdRef.current;
      if (!convId || !data.content) return;

      console.log('🔔 handleGroupChatContent:', {
        messageId: data.messageId,
        contentLength: data.content.length,
      });

      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== convId) return conv;

          const messages = conv.messages || [];
          const updatedMessages = [...messages];

          // 找到最后一个 assistant 消息并追加内容
          for (let i = updatedMessages.length - 1; i >= 0; i--) {
            if (updatedMessages[i].role === 'assistant') {
              const msg = updatedMessages[i];
              const activeChoice = msg.activeChoice || 0;
              const currentChoice = msg.choices[activeChoice];

              if (!currentChoice) break;

              const blocks = [...currentChoice.blocks];
              const lastBlock = blocks[blocks.length - 1];

              if (lastBlock && lastBlock.type === 'text') {
                blocks[blocks.length - 1] = {
                  ...lastBlock,
                  content: lastBlock.content + data.content,
                };
              } else {
                blocks.push({ type: 'text', content: data.content || '' });
              }

              const updatedChoice: Choices = { ...currentChoice, blocks };
              const updatedChoices = [...msg.choices];
              updatedChoices[activeChoice] = updatedChoice;

              updatedMessages[i] = { ...msg, choices: updatedChoices };
              break;
            }
          }

          return { ...conv, messages: updatedMessages };
        })
      );

      scrollToBottomRef.current();
    },
    [] // 🔥 空依赖数组，通过 ref 访问最新函数，避免依赖变化导致无限循环
  );

  /**
   * 处理 AI 思考内容
   */
  const handleGroupChatThink = useCallback(
    (data: GroupChatMessage) => {
      const convId = conversationIdRef.current;
      if (!convId || !data.content) return;

      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== convId) return conv;

          const messages = conv.messages || [];
          const updatedMessages = [...messages];

          for (let i = updatedMessages.length - 1; i >= 0; i--) {
            if (updatedMessages[i].role === 'assistant') {
              const msg = updatedMessages[i];
              const activeChoice = msg.activeChoice || 0;
              const currentChoice = msg.choices[activeChoice];

              if (!currentChoice) break;

              const blocks = [...currentChoice.blocks];
              const lastBlock = blocks[blocks.length - 1];

              if (lastBlock && lastBlock.type === 'think') {
                blocks[blocks.length - 1] = {
                  ...lastBlock,
                  content: lastBlock.content + data.content,
                };
              } else {
                blocks.push({ type: 'think', content: data.content || '' });
              }

              const updatedChoice: Choices = { ...currentChoice, blocks };
              const updatedChoices = [...msg.choices];
              updatedChoices[activeChoice] = updatedChoice;

              updatedMessages[i] = { ...msg, choices: updatedChoices };
              break;
            }
          }

          return { ...conv, messages: updatedMessages };
        })
      );
    },
    [] // 🔥 空依赖数组，通过 ref 访问最新函数，避免依赖变化导致无限循环
  );

  /**
   * 处理 AI 消息结束
   */
  const handleGroupChatHashCode = useCallback(
    (data: GroupChatMessage) => {
      const convId = conversationIdRef.current;
      if (!convId) return;

      console.log('🔔 handleGroupChatHashCode: AI 回复结束', {
        messageId: data.messageId,
      });

      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== convId) return conv;

          const messages = conv.messages || [];
          const updatedMessages = messages.map(msg => {
            if (msg.role === 'assistant' && msg.isStreaming) {
              return { ...msg, isStreaming: false };
            }
            return msg;
          });

          return { ...conv, messages: updatedMessages, isLoading: false };
        })
      );

      scrollToBottomRef.current();

      // 🔥 skill 会话：不移除标记（整个 conversation 永久保持 skill 模式），只清理任务队列
      const uiState = useUIStore.getState();
      if (uiState.isSkillConversation(convId)) {
        console.log(
          '🎯 Skill conversation message completed, cleaning tasks:',
          convId
        );
        // 不移除 skill 标记：uiState.removeSkillConversation(convId);
        const { taskQueue, removeTask } = uiState;
        taskQueue
          .filter(
            t =>
              t.conversationId === convId &&
              (t.status === 'sent' ||
                t.status === 'initialized' ||
                t.status === 'streaming')
          )
          .forEach(t => removeTask(t.id));
      }

      // 🔥 群聊完成后刷新侧边栏（Recent chats 和 Starred）
      window.refreshSidebarRecent?.();
      window.refreshSidebarStarred?.();
    },
    [] // 🔥 空依赖数组，通过 ref 访问最新函数，避免依赖变化导致无限循环
  );

  // 使用 useGroupChat hook 监听 WebSocket 消息
  const { isConnected, socketId } = useGroupChat({
    conversationId: conversationId || '',
    isGroupChat,
    onUserMessage: handleGroupChatUserMessage,
    onSelfMessage: handleGroupChatSelfMessage,
    onMessageInit: handleGroupChatMessageInit,
    onContent: handleGroupChatContent,
    onThink: handleGroupChatThink,
    onHashCode: handleGroupChatHashCode,
  });

  return {
    isConnected,
    socketId,
    currentUserId: currentUserIdRef.current,
  };
}

export default useGroupChatHandler;
